import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from './ui/tooltip';
import { toast } from 'sonner';
import {
  Share2, Twitter, Facebook, Linkedin, Mail, Link2, MessageSquare, Copy, Send,
  MoreHorizontal, RefreshCw, ExternalLink, QrCode
} from 'lucide-react';
import { RedditIcon, RepostDialog, CustomUrlDialog, RedditShareDialog, QRDialog } from './share/ShareDialogs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform configurations
const PLATFORMS = {
  twitter: { name: 'X (Twitter)', icon: Twitter, getUrl: (url, text) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
  facebook: { name: 'Facebook', icon: Facebook, getUrl: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  whatsapp: { name: 'WhatsApp', icon: MessageSquare, getUrl: (url, text) => `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}` },
  linkedin: { name: 'LinkedIn', icon: Linkedin, getUrl: (url, text) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}` },
  telegram: { name: 'Telegram', icon: Send, getUrl: (url, text) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
  email: { name: 'Email', icon: Mail, getUrl: (url, text) => `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}` },
  pinterest: { name: 'Pinterest', icon: Link2, getUrl: (url, text, img) => `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(text)}${img ? `&media=${encodeURIComponent(img)}` : ''}` },
};

const ShareButtons = ({
  url, title = '', description = '', source = 'share', imageUrl = null,
  petType = null, onRepost = null, showRepost = false, showCustomUrl = false,
  compact = false, className = ''
}) => {
  const [showRepostDialog, setShowRepostDialog] = useState(false);
  const [showCustomUrlDialog, setShowCustomUrlDialog] = useState(false);
  const [showRedditDialog, setShowRedditDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [sharing, setSharing] = useState(false);

  const getFullUrl = (platform = null) => {
    let baseUrl = url;
    if (!baseUrl.startsWith('http')) baseUrl = `${window.location.origin}${url}`;
    const utmParams = new URLSearchParams({
      utm_source: platform || 'share',
      utm_medium: 'social',
      utm_campaign: source
    });
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${utmParams.toString()}`;
  };

  const getCleanUrl = () => {
    let baseUrl = url;
    if (!baseUrl.startsWith('http')) baseUrl = `${window.location.origin}${url}`;
    return baseUrl;
  };

  const trackShare = async (platform) => {
    try {
      await fetch(`${API_URL}/api/shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, platform, source })
      });
    } catch {}
  };

  const handleShare = async (platform) => {
    const trackedUrl = getFullUrl(platform);
    const text = title || description || '';
    await trackShare(platform);
    const platformConfig = PLATFORMS[platform];
    if (platformConfig) {
      const shareUrl = platformConfig.getUrl(trackedUrl, text, imageUrl);
      window.open(shareUrl, '_blank', 'width=600,height=400,noopener,noreferrer');
    }
  };

  const handleCopyLink = async () => {
    try {
      const trackedUrl = getFullUrl('copy');
      await navigator.clipboard.writeText(trackedUrl);
      await trackShare('copy');
      toast.success('Link copied!');
    } catch { toast.error('Failed to copy'); }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || '', text: description, url: getFullUrl() });
        await trackShare('native');
      } catch (err) {
        if (err.name !== 'AbortError') toast.error('Failed to share');
      }
    }
  };

  const handleRepost = async (comment) => {
    if (!onRepost) return;
    setSharing(true);
    try {
      await onRepost(comment, getFullUrl());
      await trackShare('repost');
      setShowRepostDialog(false);
      toast.success('Reposted successfully!');
    } catch { toast.error('Failed to repost'); }
    finally { setSharing(false); }
  };

  const handleRedditSubmit = async (subreddit, postTitle) => {
    const trackedUrl = getFullUrl('reddit');
    await trackShare('reddit');
    try {
      await fetch(`${API_URL}/api/subreddits/track-share?subreddit=${encodeURIComponent(subreddit)}`, { method: 'POST' });
    } catch {}
    const redditUrl = `https://www.reddit.com/r/${subreddit}/submit?url=${encodeURIComponent(trackedUrl)}&title=${encodeURIComponent(postTitle)}`;
    window.open(redditUrl, '_blank', 'noopener,noreferrer');
    setShowRedditDialog(false);
    toast.success('Opening Reddit... (login may be required)');
  };

  const handleCustomUrlSubmit = async (customUrl) => {
    await trackShare('custom_url');
    window.open(customUrl, '_blank', 'noopener,noreferrer');
    setShowCustomUrlDialog(false);
  };

  // Compact mode
  if (compact) {
    return (
      <TooltipProvider>
        <div className={`flex items-center gap-1 ${className}`}>
          <Tooltip><TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-black hover:text-white" onClick={() => handleShare('twitter')} data-testid="share-twitter">
              <Twitter className="w-4 h-4" />
            </Button>
          </TooltipTrigger><TooltipContent>X (Twitter)</TooltipContent></Tooltip>

          <Tooltip><TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-[#1877F2] hover:text-white" onClick={() => handleShare('facebook')} data-testid="share-facebook">
              <Facebook className="w-4 h-4" />
            </Button>
          </TooltipTrigger><TooltipContent>Facebook</TooltipContent></Tooltip>

          <Tooltip><TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-[#25D366] hover:text-white" onClick={() => handleShare('whatsapp')} data-testid="share-whatsapp">
              <MessageSquare className="w-4 h-4" />
            </Button>
          </TooltipTrigger><TooltipContent>WhatsApp</TooltipContent></Tooltip>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" data-testid="share-more">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {['linkedin', 'telegram', 'email', 'pinterest'].map(p => (
                <DropdownMenuItem key={p} onClick={() => handleShare(p)} className="cursor-pointer">
                  {React.createElement(PLATFORMS[p].icon, { className: "w-4 h-4 mr-3" })}
                  {PLATFORMS[p].name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer"><Copy className="w-4 h-4 mr-3" />Copy Link</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowRedditDialog(true); }} className="cursor-pointer text-[#FF4500]"><RedditIcon className="w-4 h-4 mr-3" />Post to Reddit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowQRDialog(true)} className="cursor-pointer"><QrCode className="w-4 h-4 mr-3" />QR Code</DropdownMenuItem>
              {navigator.share && <DropdownMenuItem onClick={handleNativeShare} className="cursor-pointer"><Send className="w-4 h-4 mr-3" />More Options...</DropdownMenuItem>}
              {showRepost && onRepost && (
                <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => setShowRepostDialog(true)} className="cursor-pointer text-green-600"><RefreshCw className="w-4 h-4 mr-3" />Repost</DropdownMenuItem></>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <RepostDialog open={showRepostDialog} onOpenChange={setShowRepostDialog} title={title} description={description} onRepost={handleRepost} sharing={sharing} />
          <RedditShareDialog open={showRedditDialog} onOpenChange={setShowRedditDialog} title={title} onSubmit={handleRedditSubmit} petType={petType} />
          <QRDialog open={showQRDialog} onOpenChange={setShowQRDialog} url={url} title={title} source={source} />
        </div>
      </TooltipProvider>
    );
  }

  // Full mode
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={className} data-testid="share-button">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {Object.entries(PLATFORMS).map(([key, platform]) => (
            <DropdownMenuItem key={key} onClick={() => handleShare(key)} className="cursor-pointer">
              <platform.icon className="w-4 h-4 mr-3" />{platform.name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer"><Copy className="w-4 h-4 mr-3" />Copy Link</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowRedditDialog(true)} className="cursor-pointer text-[#FF4500]"><RedditIcon className="w-4 h-4 mr-3" />Post to Reddit</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowQRDialog(true)} className="cursor-pointer"><QrCode className="w-4 h-4 mr-3" />QR Code</DropdownMenuItem>
          {navigator.share && <DropdownMenuItem onClick={handleNativeShare} className="cursor-pointer"><Send className="w-4 h-4 mr-3" />More Options...</DropdownMenuItem>}
          {showRepost && onRepost && (
            <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => setShowRepostDialog(true)} className="cursor-pointer text-green-600"><RefreshCw className="w-4 h-4 mr-3" />Repost with Comment</DropdownMenuItem></>
          )}
          {showCustomUrl && (
            <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => setShowCustomUrlDialog(true)} className="cursor-pointer"><ExternalLink className="w-4 h-4 mr-3" />Share to Custom URL</DropdownMenuItem></>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <RepostDialog open={showRepostDialog} onOpenChange={setShowRepostDialog} title={title} description={description} onRepost={handleRepost} sharing={sharing} />
      <CustomUrlDialog open={showCustomUrlDialog} onOpenChange={setShowCustomUrlDialog} onSubmit={handleCustomUrlSubmit} />
      <RedditShareDialog open={showRedditDialog} onOpenChange={setShowRedditDialog} title={title} onSubmit={handleRedditSubmit} petType={petType} />
      <QRDialog open={showQRDialog} onOpenChange={setShowQRDialog} url={url} title={title} source={source} />
    </>
  );
};

export default ShareButtons;
